import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import Dialog, { ModalOpeningLoader } from './TivigoDialog';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { createTivigoCryptoRequestId } from './creditCheckoutApi';
import { loadPackagePricing } from './services/packagePricing';
import { getCreditCurrency, normalizeEurTryRate } from './creditCurrency';
import { getCreditPackagePresets } from './creditPackagePresets';
import { creditTopUpAmountTry } from './creditTopUpAmount';
import './userPanelCreditModal.css';

const MIN_AMOUNT = 50;
const MAX_AMOUNT = 100000;
const CREDIT_REGION_NOTICES = {
  EUR: {
    title: 'Hesabınız tüm dünyadan kullanılabilir.',
    detail: 'Avrupa tarifesi uygulanır; ödeme tutarı TL olarak hesaplanır. Bu ekranda yalnızca Türkiye’deki bankalara ait kartlarla ödeme yapabilirsiniz. Yurt dışı kartıyla ödeme yapmak için canlı destekle iletişime geçin.',
  },
  TRY: {
    title: 'Hesabınız yalnızca Türkiye’den izlenebilir.',
    detail: 'Türkiye tarifesi uygulanır. Hem Türkiye’den hem de Avrupa’dan izlemek istiyorsanız, hesabınızın yurt dışı kullanımına açılması için canlı destek ekibimizle iletişime geçin.',
  },
};
const DEFAULT_PAYMENT_ORIGINS = ['suphero.io', 'eximhost.com', '51.68.34.169'];
const DEFAULT_CHECKOUT_FRAME_HEIGHT = 500;
const POLPAY_CHECKOUT_FRAME_HEIGHT = 680;
const PAYIZONE_CHECKOUT_FRAME_HEIGHT = 680;
const PAYIZONE_STATUS_POLL_INTERVAL_MS = 5_000;
const PAYIZONE_MERCHANT_ORDER_PATTERN = /^tivigo-credit-\d+-[a-f0-9]{32}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MIN_REMOTE_CHECKOUT_FRAME_HEIGHT = 320;
const MAX_REMOTE_CHECKOUT_FRAME_HEIGHT = POLPAY_CHECKOUT_FRAME_HEIGHT;
const CUSTOMER_PROFILE_INCOMPLETE_CODE = 'customer_profile_incomplete';
const CUSTOMER_NAME_PATTERN = /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s-]{2,50}$/u;

const normalizeCustomerName = (value) => String(value || '')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .map((word) => `${word.charAt(0).toLocaleUpperCase('tr-TR')}${word.slice(1).toLocaleLowerCase('tr-TR')}`)
  .join(' ');

const isValidCustomerName = (value) => CUSTOMER_NAME_PATTERN.test(
  normalizeCustomerName(value),
);

const normalizeRemoteCheckoutFrameHeight = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;

  return Math.min(
    MAX_REMOTE_CHECKOUT_FRAME_HEIGHT,
    Math.max(MIN_REMOTE_CHECKOUT_FRAME_HEIGHT, Math.round(value)),
  );
};

const formatCredit = (value) => new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  maximumFractionDigits: 2,
}).format(value);

const getFirstNumber = (...values) => {
  for (const value of values) {
    if (value === '' || value == null) continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
};

const getCreditAccount = (userInfo, previewMode) => {
  const info = userInfo?.user_info || {};
  const user = userInfo?.user || {};
  const currentCredit = getFirstNumber(
    info.kredi,
    info.credit,
    info.credits,
    info.balance,
    user.kredi,
    user.credit,
    userInfo?.kredi,
  );

  return {
    id: info.id || user.id || userInfo?.id || info.username || '',
    email: info.email || user.email || userInfo?.email || '',
    name: info.ad || info.name || user.ad || user.name || userInfo?.ad || userInfo?.name || '',
    team: info.takim || info.team || user.takim || user.team || userInfo?.takim || userInfo?.team || '',
    maxConnections: getFirstNumber(user.max_connections, info.max_connections, userInfo?.max_connections) ?? 1,
    currentCredit: currentCredit == null && previewMode ? 0 : currentCredit,
  };
};

function UserPanelCreditModal({
  open,
  onClose,
  userInfo,
  previewMode = false,
  isReseller = false,
  resellerDiscount = 0,
  flow,
  provider,
  allowedPaymentOrigins = DEFAULT_PAYMENT_ORIGINS,
  onStartCheckout,
  onStartCryptoCheckout,
  onGetCryptoCheckoutStatus,
  onUpdateProfile,
  onVerifyPaymentResult,
  onPaymentResult,
  onOpenWhatsapp,
}) {
  const regionArtId = useId();
  const flowType = flow?.type || 'stripe';
  const [amount, setAmount] = useState('');
  const [cardConfirmation, setCardConfirmation] = useState(null);
  const confirmationFocusRef = useRef(null);
  const cardButtonRef = useRef(null);
  const confirmationWasOpen = useRef(false);
  const [submittedAmountTry, setSubmittedAmountTry] = useState(null);
  const [presetConnections, setPresetConnections] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const selectedDurationRef = useRef(null);
  selectedDurationRef.current = selectedDuration;
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState('');
  const [pricingAttempt, setPricingAttempt] = useState(0);
  const [phase, setPhase] = useState('form');
  const [loading, setLoading] = useState(false);
  const [activePaymentMethod, setActivePaymentMethod] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [checkoutProvider, setCheckoutProvider] = useState('');
  const [remoteCheckoutFrameHeight, setRemoteCheckoutFrameHeight] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const [cryptoCheckoutReference, setCryptoCheckoutReference] = useState(null);
  const [profileNameDialogOpen, setProfileNameDialogOpen] = useState(false);
  const [profileNameValue, setProfileNameValue] = useState('');
  const [profileNameError, setProfileNameError] = useState('');
  const [profileNameSaving, setProfileNameSaving] = useState(false);
  const [profileNameOverride, setProfileNameOverride] = useState('');
  const iframeRef = useRef(null);
  const paymentProcessedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const cryptoRequestIdRef = useRef('');
  const checkoutStageRef = useRef('checkout');

  const account = useMemo(() => {
    const derivedAccount = getCreditAccount(userInfo, previewMode);
    return {
      ...derivedAccount,
      id: flow?.userId || derivedAccount.id,
      email: flow?.userEmail || derivedAccount.email,
    };
  }, [flow?.userEmail, flow?.userId, previewMode, userInfo]);
  const { currency: accountCurrency, eurTryRate: accountEurTryRate } = getCreditCurrency(userInfo, isReseller);
  const noticeCurrency = isReseller ? 'TRY' : pricing?.currency || accountCurrency;
  const inputCurrency = noticeCurrency === 'EUR' ? 'EUR' : 'TRY';
  const inputCurrencyLabel = inputCurrency === 'EUR' ? '€' : 'TL';
  const eurTryRate = normalizeEurTryRate(previewMode ? accountEurTryRate : pricing?.eur_try_rate);
  const previousInputCurrency = useRef(inputCurrency);
  const creditRegionNotice = CREDIT_REGION_NOTICES[noticeCurrency];
  const presets = getCreditPackagePresets({
    pricing, maxConnections: presetConnections, isReseller, resellerDiscount,
  });
  const amountNumber = creditTopUpAmountTry(amount, inputCurrency, eurTryRate);
  const hasAmount = amountNumber !== null;
  const amountIsValid = hasAmount && amountNumber >= MIN_AMOUNT && amountNumber <= MAX_AMOUNT;
  const resultAmountTry = submittedAmountTry ?? amountNumber;
  const effectiveProfileName = profileNameOverride || account.name;
  const profileNameIsValid = isValidCustomerName(effectiveProfileName);
  const isPolPayCheckout = checkoutProvider === 'polpay' || flowType === 'polpay';
  const isPayizoneCryptoCheckout = checkoutProvider === 'payizone-crypto';
  const fallbackCheckoutFrameHeight = isPolPayCheckout
    ? POLPAY_CHECKOUT_FRAME_HEIGHT
    : isPayizoneCryptoCheckout
      ? PAYIZONE_CHECKOUT_FRAME_HEIGHT
      : DEFAULT_CHECKOUT_FRAME_HEIGHT;
  const checkoutFrameHeight = remoteCheckoutFrameHeight ?? fallbackCheckoutFrameHeight;

  const amountError = useMemo(() => {
    if (amount === '') return '';
    if (inputCurrency === 'EUR' && eurTryRate === null) return 'Euro kuru doğrulanamadı. Lütfen yeniden deneyin.';
    if (amountNumber === null) return 'Geçerli bir tutar girin. En fazla iki ondalık basamak kullanabilirsiniz.';
    if (amountNumber < MIN_AMOUNT) return `Minimum ${MIN_AMOUNT} TL yükleyebilirsiniz.`;
    if (amountNumber > MAX_AMOUNT) return `Maksimum ${formatCredit(MAX_AMOUNT)} TL yükleyebilirsiniz.`;
    return '';
  }, [amount, amountNumber, eurTryRate, inputCurrency]);

  useEffect(() => {
    if (!open) return;

    setAmount('');
    setCardConfirmation(null);
    setSubmittedAmountTry(null);
    setSelectedDuration(null);
    setPhase(flowType === 'whatsapp' ? 'contact' : 'form');
    setLoading(false);
    setActivePaymentMethod('');
    setCheckoutUrl('');
    setCheckoutProvider('');
    setRemoteCheckoutFrameHeight(null);
    checkoutStageRef.current = 'checkout';
    setIframeReady(false);
    setRequestError('');
    setPaymentResult(null);
    setCryptoCheckoutReference(null);
    setProfileNameDialogOpen(false);
    setProfileNameValue('');
    setProfileNameError('');
    setProfileNameSaving(false);
    setProfileNameOverride('');
    paymentProcessedRef.current = false;
    submitInFlightRef.current = false;
    cryptoRequestIdRef.current = '';
  }, [account.id, flowType, open]);

  useEffect(() => {
    if (!open) return;
    setPresetConnections([1, 2].includes(account.maxConnections) ? account.maxConnections : null);
  }, [account.id, account.maxConnections, open]);

  useEffect(() => {
    if (previousInputCurrency.current !== inputCurrency) {
      setAmount('');
      setSelectedDuration(null);
      previousInputCurrency.current = inputCurrency;
    }
  }, [inputCurrency]);

  useEffect(() => {
    setCardConfirmation(null);
  }, [account.id, amount, inputCurrency, eurTryRate, open, presetConnections, pricingLoading, selectedDuration]);

  useEffect(() => {
    if (cardConfirmation) confirmationFocusRef.current?.focus();
    else if (confirmationWasOpen.current) cardButtonRef.current?.focus();
    confirmationWasOpen.current = Boolean(cardConfirmation);
  }, [cardConfirmation]);

  useEffect(() => {
    let cancelled = false;
    setPricing(null);
    setPricingError('');
    setPricingLoading(false);
    if (selectedDurationRef.current !== null) setAmount('');
    setSelectedDuration(null);
    if (!open || flowType === 'whatsapp' || previewMode) return undefined;

    setPricingLoading(true);
    loadPackagePricing().then((catalog) => {
      if (cancelled) return;
      if (!catalog.available || catalog.pricing_source !== 'account'
        || String(catalog.account_id) !== String(account.id)) {
        throw new Error('Hesap tarifesi doğrulanamadı.');
      }
      // Bayi indirimi yalnızca sunucunun da bayi saydığı hesapta uygulanır.
      if (Boolean(catalog.is_reseller) !== Boolean(isReseller)) throw new Error('Bayi tarifesi için hesap bilgilerini yenileyin.');
      if (catalog.currency === 'EUR' && !catalog.eur_try_rate) {
        throw new Error('Euro kuru alınamadı.');
      }
      setPricing(catalog);
    }).catch((error) => {
      if (!cancelled) setPricingError(error.message || 'Paket fiyatları alınamadı.');
    }).finally(() => {
      if (!cancelled) setPricingLoading(false);
    });
    return () => { cancelled = true; };
  }, [account.id, account.maxConnections, accountCurrency, flowType, isReseller, open, previewMode, pricingAttempt, resellerDiscount]);

  useEffect(() => {
    setRemoteCheckoutFrameHeight(null);
    checkoutStageRef.current = 'checkout';
  }, [checkoutProvider, flow?.provider, flowType, open, provider]);

  useEffect(() => {
    if (!open || phase !== 'checkout' || previewMode || !checkoutUrl) return undefined;

    const handlePaymentMessage = async (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;

      let originIsAllowed = event.origin === window.location.origin;
      try {
        const hostname = new URL(event.origin).hostname;
        originIsAllowed = originIsAllowed
          || allowedPaymentOrigins.some((allowedOrigin) => (
            hostname === allowedOrigin || hostname.endsWith(`.${allowedOrigin}`)
          ))
          || ['localhost', '127.0.0.1'].includes(hostname);
      } catch (error) {
        // Aynı origin kontrolü URL ayrıştırılamasa da geçerli kalır.
      }

      if (!originIsAllowed || !event.data) return;
      if (event.data.type === 'eximhostCheckoutReady') {
        setIframeReady(true);
        return;
      }
      const checkoutProtocolMessageIsAllowed = isPolPayCheckout
        && event.data.version === 1
        && event.data.provider === 'polpay'
        && (() => {
          try {
            return event.origin === new URL(checkoutUrl).origin;
          } catch (error) {
            return false;
          }
        })();

      if (event.data.type === 'checkoutResize') {
        if (!checkoutProtocolMessageIsAllowed
          || event.data.stage !== 'checkout'
          || checkoutStageRef.current !== 'checkout') return;
        const nextHeight = normalizeRemoteCheckoutFrameHeight(event.data.height);
        if (nextHeight != null) setRemoteCheckoutFrameHeight(nextHeight);
        return;
      }
      if (event.data.type === 'checkoutStage') {
        if (checkoutProtocolMessageIsAllowed && event.data.stage === '3ds') {
          checkoutStageRef.current = '3ds';
          setRemoteCheckoutFrameHeight(POLPAY_CHECKOUT_FRAME_HEIGHT);
        }
        return;
      }
      if (event.data.type !== 'paymentStatus') return;

      if (paymentProcessedRef.current) {
        try {
          event.source?.postMessage({ type: 'paymentACK' }, event.origin);
        } catch (error) {
          // İşlem zaten işlendi; tekrar mesajı yalnızca ACK ile susturulur.
        }
        return;
      }

      paymentProcessedRef.current = true;
      try {
        event.source?.postMessage({ type: 'paymentACK' }, event.origin);
      } catch (error) {
        // Ödeme sonucu yine de gösterilir.
      }

      const reportedAmount = Number(event.data.amount);
      const resultAmount = Number.isFinite(reportedAmount) && reportedAmount > 0
        ? reportedAmount
        : resultAmountTry;
      const signaledResult = {
        success: Boolean(event.data.success),
        message: event.data.message || '',
        amount: resultAmount,
        provider: checkoutProvider || provider || flow?.provider || '',
        transactionId: event.data.transactionId || event.data.transaction_id || event.data.orderId || '',
      };

      setLoading(true);
      let result = signaledResult;
      if (typeof onVerifyPaymentResult === 'function') {
        try {
          const verifiedResult = await onVerifyPaymentResult(signaledResult);
          result = {
            ...signaledResult,
            ...verifiedResult,
            success: verifiedResult?.success === true,
          };
        } catch (error) {
          result = {
            ...signaledResult,
            success: false,
            message: error.message || 'Ödeme sonucu doğrulanamadı.',
          };
        }
      }

      setPaymentResult(result);
      setPhase('result');
      setCheckoutUrl('');
      setRemoteCheckoutFrameHeight(null);
      checkoutStageRef.current = 'checkout';
      setLoading(false);
      Promise.resolve(onPaymentResult?.(result)).catch(() => {});
    };

    window.addEventListener('message', handlePaymentMessage);
    return () => window.removeEventListener('message', handlePaymentMessage);
  }, [allowedPaymentOrigins, resultAmountTry, checkoutProvider, checkoutUrl, flow?.provider, isPolPayCheckout, onPaymentResult, onVerifyPaymentResult, open, phase, previewMode, provider]);

  useEffect(() => {
    if (!open
      || phase !== 'checkout'
      || previewMode
      || !isPayizoneCryptoCheckout
      || !cryptoCheckoutReference?.merchantOrderId
      || typeof onGetCryptoCheckoutStatus !== 'function') {
      return undefined;
    }

    let active = true;
    let timer = null;
    function scheduleNext() {
      if (!active) return;
      timer = window.setTimeout(pollStatus, PAYIZONE_STATUS_POLL_INTERVAL_MS);
    }
    async function pollStatus() {
      try {
        const statusResult = await onGetCryptoCheckoutStatus({
          merchantOrderId: cryptoCheckoutReference.merchantOrderId,
          orderId: cryptoCheckoutReference.orderId,
        });
        if (!active) return;

        if (statusResult?.credited === true) {
          if (paymentProcessedRef.current) return;
          paymentProcessedRef.current = true;
          const reportedAmount = Number(statusResult.amount);
          const result = {
            success: true,
            message: 'Kripto ödemeniz doğrulandı ve krediniz yüklendi.',
            amount: Number.isFinite(reportedAmount) && reportedAmount > 0
              ? reportedAmount
              : resultAmountTry,
            provider: 'payizone-crypto',
            transactionId: statusResult.orderId || cryptoCheckoutReference.orderId,
            merchantOrderId: cryptoCheckoutReference.merchantOrderId,
          };
          setPaymentResult(result);
          setPhase('result');
          setCheckoutUrl('');
          setCryptoCheckoutReference(null);
          setRemoteCheckoutFrameHeight(null);
          setLoading(false);
          Promise.resolve(onPaymentResult?.(result)).catch(() => {});
          return;
        }

        if (statusResult?.terminal !== true) scheduleNext();
      } catch (error) {
        scheduleNext();
      }
    }

    pollStatus();
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [resultAmountTry, cryptoCheckoutReference, isPayizoneCryptoCheckout, onGetCryptoCheckoutStatus, onPaymentResult, open, phase, previewMode]);

  const handleExplicitClose = () => {
    setCardConfirmation(null);
    setProfileNameDialogOpen(false);
    setRemoteCheckoutFrameHeight(null);
    checkoutStageRef.current = 'checkout';
    onClose?.();
  };

  const handleAmountChange = (event) => {
    setAmount(event.target.value);
    setSelectedDuration(null);
    setRequestError('');
    cryptoRequestIdRef.current = '';
  };

  const handlePresetSelect = ({ duration, amount: presetAmount, nativeAmount }) => {
    if (loading || pricingLoading || presetAmount === null
      || presetAmount < MIN_AMOUNT || presetAmount > MAX_AMOUNT) return;
    setAmount(String(inputCurrency === 'EUR' ? nativeAmount : presetAmount));
    setSelectedDuration(duration);
    setRequestError('');
    cryptoRequestIdRef.current = '';
  };

  const handlePresetConnectionsChange = (connections) => {
    if (loading || connections === presetConnections) return;
    setPresetConnections(connections);
    if (selectedDuration === null) return;

    const nextPreset = getCreditPackagePresets({
      pricing, maxConnections: connections, isReseller, resellerDiscount,
    }).find((preset) => preset.duration === selectedDuration);
    if (!pricingLoading && nextPreset?.amount != null
      && nextPreset.amount >= MIN_AMOUNT && nextPreset.amount <= MAX_AMOUNT) {
      setAmount(String(inputCurrency === 'EUR' ? nextPreset.nativeAmount : nextPreset.amount));
    } else {
      setAmount('');
      setSelectedDuration(null);
    }
    setRequestError('');
    cryptoRequestIdRef.current = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!open || phase !== 'form' || !amountIsValid || submitInFlightRef.current) return;
    if (inputCurrency === 'EUR') {
      setCardConfirmation({ nativeAmount: Number(amount), amountTry: amountNumber, rate: eurTryRate, accountId: account.id });
      return;
    }
    await startCardCheckout(amountNumber);
  };

  const confirmCardCheckout = async () => {
    if (!cardConfirmation || submitInFlightRef.current) return;
    if (!open || phase !== 'form' || !amountIsValid || inputCurrency !== 'EUR'
      || cardConfirmation.nativeAmount !== Number(amount) || cardConfirmation.amountTry !== amountNumber
      || cardConfirmation.rate !== eurTryRate || cardConfirmation.accountId !== account.id) {
      setCardConfirmation(null);
      return;
    }
    const confirmedAmount = cardConfirmation.amountTry;
    setCardConfirmation(null);
    await startCardCheckout(confirmedAmount);
  };

  const startCardCheckout = async (chargeAmountTry) => {
    if (submitInFlightRef.current) return;
    setRequestError('');
    setSubmittedAmountTry(chargeAmountTry);
    paymentProcessedRef.current = false;
    setRemoteCheckoutFrameHeight(null);
    checkoutStageRef.current = 'checkout';

    if (previewMode) {
      setPhase('checkout');
      return;
    }

    if (typeof onStartCheckout !== 'function') {
      setRequestError('Ödeme sağlayıcısı henüz bu temaya bağlanmadı.');
      return;
    }

    submitInFlightRef.current = true;
    setLoading(true);
    setActivePaymentMethod('card');
    try {
      const checkout = await onStartCheckout({
        amount: chargeAmountTry,
        userId: account.id,
        userEmail: account.email,
        provider: provider || flow?.provider,
        flow,
      });
      const url = typeof checkout === 'string'
        ? checkout
        : checkout?.checkoutUrl
          || checkout?.checkout_url
          || checkout?.redirectUrl
          || checkout?.paymentUrl
          || checkout?.payment_url
          || checkout?.data?.checkout_url
          || checkout?.data?.payment_url;

      if (!url) throw new Error('Güvenli ödeme bağlantısı alınamadı.');

      const parsedUrl = new URL(url);
      const isAllowedHost = allowedPaymentOrigins.some((allowedOrigin) => (
        parsedUrl.hostname === allowedOrigin || parsedUrl.hostname.endsWith(`.${allowedOrigin}`)
      ));
      const isLocalDevelopment = process.env.NODE_ENV !== 'production'
        && ['localhost', '127.0.0.1'].includes(parsedUrl.hostname);

      if ((!isAllowedHost && !isLocalDevelopment) || (parsedUrl.protocol !== 'https:' && !isLocalDevelopment)) {
        throw new Error('Ödeme bağlantısı güvenlik doğrulamasından geçemedi.');
      }

      setCheckoutUrl(parsedUrl.toString());
      setCheckoutProvider(checkout?.provider || provider || flow?.provider || '');
      setIframeReady(false);
      setPhase('checkout');
    } catch (error) {
      setRequestError(error.message || 'Ödeme başlatılamadı. Lütfen tekrar deneyin.');
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
      setActivePaymentMethod('');
    }
  };

  const openProfileNameDialog = () => {
    setProfileNameValue(effectiveProfileName || '');
    setProfileNameError('');
    setRequestError('');
    setProfileNameDialogOpen(true);
  };

  const closeProfileNameDialog = () => {
    if (profileNameSaving) return;
    setProfileNameDialogOpen(false);
    setProfileNameError('');
  };

  const startCryptoCheckout = async ({ customerName = effectiveProfileName } = {}) => {
    if (!amountIsValid || submitInFlightRef.current) return;
    if (!cryptoRequestIdRef.current) {
      cryptoRequestIdRef.current = createTivigoCryptoRequestId();
    }

    submitInFlightRef.current = true;
    setSubmittedAmountTry(amountNumber);
    setLoading(true);
    setActivePaymentMethod('crypto');
    try {
      const checkout = await onStartCryptoCheckout({
        amount: amountNumber,
        userId: account.id,
        userEmail: account.email,
        requestId: cryptoRequestIdRef.current,
      });
      const url = typeof checkout === 'string'
        ? checkout
        : checkout?.checkoutUrl || checkout?.checkout_url;
      if (!url) throw new Error('Kripto ödeme bağlantısı alınamadı.');

      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname.toLowerCase();
      const isPayizoneHost = hostname === 'payizone.com' || hostname.endsWith('.payizone.com');
      if (parsedUrl.protocol !== 'https:'
        || !isPayizoneHost
        || !parsedUrl.pathname.startsWith('/crypto/pay/')
        || parsedUrl.username
        || parsedUrl.password) {
        throw new Error('Kripto ödeme bağlantısı güvenlik doğrulamasından geçemedi.');
      }

      const merchantOrderId = String(
        checkout?.merchantOrderId || checkout?.merchant_order_id || '',
      ).trim();
      const orderId = String(checkout?.orderId || checkout?.order_id || '').trim();
      if (!PAYIZONE_MERCHANT_ORDER_PATTERN.test(merchantOrderId)
        || !UUID_PATTERN.test(orderId)) {
        throw new Error('Kripto ödeme servisinden geçersiz sipariş cevabı alındı.');
      }

      setCheckoutUrl(parsedUrl.toString());
      setCheckoutProvider('payizone-crypto');
      setCryptoCheckoutReference({ merchantOrderId, orderId });
      setRemoteCheckoutFrameHeight(null);
      setIframeReady(false);
      setPhase('checkout');
    } catch (error) {
      if (error?.code === CUSTOMER_PROFILE_INCOMPLETE_CODE
        && !isValidCustomerName(customerName)) {
        openProfileNameDialog();
      } else if (error?.code === CUSTOMER_PROFILE_INCOMPLETE_CODE) {
        setRequestError('Kripto ödeme için profilinizde geçerli bir e-posta adresi bulunmalıdır. E-posta bilginizi Hesap bölümünden tamamlayın.');
      } else {
        setRequestError(error.message || 'Kripto ödeme başlatılamadı. Lütfen tekrar deneyin.');
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
      setActivePaymentMethod('');
    }
  };

  const handleCryptoCheckout = async () => {
    if (!amountIsValid || submitInFlightRef.current) return;

    setRequestError('');
    if (previewMode) {
      setRequestError('Kripto ödeme, canlı kullanıcı oturumunda Payizone ekranını bu pencerede açar.');
      return;
    }
    if (typeof onStartCryptoCheckout !== 'function') {
      setRequestError('Kripto ödeme servisi henüz bu temaya bağlanmadı.');
      return;
    }
    if (!profileNameIsValid) {
      openProfileNameDialog();
      return;
    }

    await startCryptoCheckout();
  };

  const handleProfileNameSubmit = async (event) => {
    event.preventDefault();
    if (profileNameSaving) return;

    const normalizedName = normalizeCustomerName(profileNameValue);
    if (!CUSTOMER_NAME_PATTERN.test(normalizedName)) {
      setProfileNameError('Ad soyad 2-50 karakter olmalı; yalnızca harf, boşluk ve kısa çizgi kullanılabilir.');
      return;
    }
    if (typeof onUpdateProfile !== 'function') {
      setProfileNameError('Profil güncelleme bağlantısı kullanılamıyor. Lütfen tekrar deneyin.');
      return;
    }

    setProfileNameSaving(true);
    setProfileNameError('');
    try {
      const result = await onUpdateProfile({
        userInfo,
        name: normalizedName,
        team: account.team || '',
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Ad soyad bilgisi güncellenemedi.');
      }

      setProfileNameOverride(normalizedName);
      setProfileNameValue(normalizedName);
      setProfileNameDialogOpen(false);
      await startCryptoCheckout({ customerName: normalizedName });
    } catch (error) {
      setProfileNameError(error.message || 'Ad soyad bilgisi güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setProfileNameSaving(false);
    }
  };

  const handleRetry = () => {
    setPhase('form');
    setPaymentResult(null);
    setCheckoutUrl('');
    setCheckoutProvider('');
    setCryptoCheckoutReference(null);
    setActivePaymentMethod('');
    setRemoteCheckoutFrameHeight(null);
    checkoutStageRef.current = 'checkout';
    setIframeReady(false);
  };

  const handleCheckoutIframeLoad = () => {
    setIframeReady(true);
    if (!isPolPayCheckout || !checkoutUrl) return;

    try {
      const checkoutOrigin = new URL(checkoutUrl).origin;
      iframeRef.current?.contentWindow?.postMessage({
        type: 'checkoutParentReady',
        version: 1,
        provider: 'polpay',
      }, checkoutOrigin);
    } catch (error) {
      // Geçersiz checkout URL'si submit aşamasında zaten reddedilir.
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-credit-dialog"
      aria-labelledby={cardConfirmation ? 'tvup-credit-confirm-title' : 'tvup-credit-modal-title'}
      aria-describedby={cardConfirmation ? 'tvup-credit-confirm-description' : 'tvup-credit-modal-description'}
      slotProps={{
        backdrop: { className: 'tvup-credit-backdrop' },
        paper: {
          id: 'tvup-credit-modal',
          className: `tvup-credit-modal is-${phase}${isPayizoneCryptoCheckout ? ' is-payizone-crypto' : ''}`,
        },
      }}
    >
      <header className="tvup-credit-modal-header" inert={Boolean(cardConfirmation)}>
        <div className="tvup-credit-title-icon" aria-hidden="true">
          <AccountBalanceWalletRoundedIcon />
        </div>
        <div className="tvup-credit-title-copy">
          <h2 id="tvup-credit-modal-title">Kredi Yükle</h2>
          <p id="tvup-credit-modal-description">Hesabınıza dilediğiniz tutarda kredi ekleyin.</p>
        </div>
        <button
          type="button"
          className="tvup-credit-close"
          aria-label="Kredi yükleme penceresini kapat"
          onClick={handleExplicitClose}
        >
          <CloseRoundedIcon />
        </button>
      </header>

      {phase === 'contact' && (
        <div className="tvup-credit-contact">
          <h3>Destek ile iletişime geçin</h3>
          <p>
            Kredi yükleme ve ödeme işlemleriniz için WhatsApp destek hattımızla iletişime
            geçebilirsiniz. Talebiniz doğrultusunda işlemleriniz hızlı ve güvenli şekilde
            gerçekleştirilecektir.
          </p>
          <button type="button" onClick={() => onOpenWhatsapp?.(account.id)}>
            <WhatsAppIcon /> WhatsApp Destek Hattına Yaz
          </button>
        </div>
      )}

      {phase === 'form' && (
        <div className="tvup-credit-modal-body" inert={Boolean(cardConfirmation)}>
          <form className="tvup-credit-form" onSubmit={handleSubmit} noValidate>
            <div className="tvup-credit-form-content">
              <div className="tvup-credit-form-heading">
                <div>
                  <span className="tvup-credit-field-icon" aria-hidden="true"><CreditCardRoundedIcon /></span>
                  <div>
                    <small>ÖDEME TUTARI</small>
                    <h3>Ne kadar kredi yüklemek istersiniz?</h3>
                  </div>
                </div>
                <span>Min. {MIN_AMOUNT} TL</span>
              </div>

              <label className="tvup-credit-amount-label" htmlFor="tvup-credit-amount">Yüklenecek Tutar</label>
              <div className={`tvup-credit-amount-field${amountError ? ' has-error' : ''}`}>
                <input
                  id="tvup-credit-amount"
                  type="number"
                  min={inputCurrency === 'EUR' ? 0.01 : MIN_AMOUNT}
                  max={inputCurrency === 'EUR' ? undefined : MAX_AMOUNT}
                  step="0.01"
                  inputMode="decimal"
                  autoComplete="off"
                  autoFocus
                  value={amount}
                  disabled={loading}
                  aria-invalid={Boolean(amountError)}
                  aria-describedby={amountError ? 'tvup-credit-amount-help' : undefined}
                  onChange={handleAmountChange}
                />
                <span>{inputCurrencyLabel}</span>
              </div>
              <div className="tvup-credit-presets-heading">
                <div id="tvup-credit-presets-label" className="tvup-credit-presets-label">Hızlı Tutar Seçimi</div>
                <div className="tvup-credit-preset-connections" role="group" aria-label="Paket kişi sayısı">
                  {[1, 2].map((connections) => (
                    <button
                      key={connections}
                      type="button"
                      aria-pressed={presetConnections === connections}
                      disabled={loading}
                      onClick={() => handlePresetConnectionsChange(connections)}
                    >
                      {connections} Kişilik
                    </button>
                  ))}
                </div>
              </div>
              <div className="tvup-credit-presets" role="group" aria-labelledby="tvup-credit-presets-label" aria-busy={pricingLoading}>
                {presets.map((preset) => {
                  const available = preset.amount !== null && preset.amount >= MIN_AMOUNT && preset.amount <= MAX_AMOUNT;
                  const presetLabel = preset.duration === 999 ? 'Sınırsız Paket' : `${preset.duration} Ay`;
                  return (
                    <button
                      key={preset.duration}
                      type="button"
                      aria-pressed={selectedDuration === preset.duration}
                      disabled={loading || pricingLoading || !available}
                      title={pricingLoading ? 'Paket fiyatları yükleniyor' : available
                        ? `${presetConnections} kişilik / ${presetLabel}: ${formatCredit(inputCurrency === 'EUR' ? preset.nativeAmount : preset.amount)} ${inputCurrencyLabel}`
                        : pricingError || 'Bu süre için uygun paket tutarı bulunmuyor'}
                      onClick={() => handlePresetSelect(preset)}
                    >
                      {presetLabel}
                    </button>
                  );
                })}
              </div>
              {pricingError && (
                <div className="tvup-credit-presets-error" role="status">
                  <span>{pricingError}</span>
                  <button type="button" title="Paket fiyatlarını yeniden yükle" aria-label="Paket fiyatlarını yeniden yükle" onClick={() => setPricingAttempt((attempt) => attempt + 1)}>
                    <RefreshRoundedIcon />
                  </button>
                </div>
              )}
              {amountError && (
                <div id="tvup-credit-amount-help" className="tvup-credit-field-help has-error" role="alert">
                  {amountError}
                </div>
              )}

              <div className={`tvup-credit-info-note${noticeCurrency === 'EUR' ? ' is-global' : noticeCurrency === 'TRY' ? ' is-turkey' : ''}`} aria-live="polite">
                <div className="tvup-credit-info-copy">
                  {creditRegionNotice ? (
                    <>
                      <b>{creditRegionNotice.title}</b>
                      <span>{creditRegionNotice.detail}</span>
                    </>
                  ) : (
                    <span>{pricingError ? 'Hesap tarifeniz doğrulanamadı.' : 'Hesap tarifeniz doğrulanıyor.'}</span>
                  )}
                </div>
                {creditRegionNotice && (
                  <span className="tvup-credit-region-art" aria-hidden="true">
                    <svg className="tvup-credit-art-defs" width="0" height="0" focusable="false">
                      <defs>
                        <linearGradient id={`${regionArtId}-paint`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={noticeCurrency === 'EUR' ? '#1859b7' : '#f13c48'} />
                          <stop offset="100%" stopColor={noticeCurrency === 'EUR' ? '#003399' : '#d61f3c'} />
                        </linearGradient>
                      </defs>
                    </svg>
                    {noticeCurrency === 'EUR' ? (
                      <svg className="tvup-credit-eu-flag" viewBox="0 0 30 20" focusable="false">
                        <rect width="30" height="20" rx="1" fill={`url(#${regionArtId}-paint)`} />
                        {Array.from({ length: 12 }, (_, index) => (
                          <polygon
                            key={index}
                            points="0,-1.1 0.25,-0.34 1.05,-0.34 0.4,0.13 0.65,0.89 0,0.42 -0.65,0.89 -0.4,0.13 -1.05,-0.34 -0.25,-0.34"
                            transform={`translate(${15 + 6 * Math.sin(index * Math.PI / 6)} ${10 - 6 * Math.cos(index * Math.PI / 6)})`}
                            fill="#ffcc00"
                          />
                        ))}
                      </svg>
                    ) : (
                      <svg className="tvup-credit-turkey-flag" viewBox="0 0 30 20" focusable="false">
                        <defs>
                          <mask id={`${regionArtId}-crescent`}>
                            <rect width="30" height="20" fill="#fff" />
                            <circle cx="12" cy="10" r="4" fill="#000" />
                          </mask>
                        </defs>
                        <rect width="30" height="20" rx="1" fill={`url(#${regionArtId}-paint)`} />
                        <circle cx="10" cy="10" r="5" fill="#fff" mask={`url(#${regionArtId}-crescent)`} />
                        <polygon points="14.5,10 16.23,9.44 16.23,7.62 17.3,9.09 19.02,8.53 17.95,10 19.02,11.47 17.3,10.91 16.23,12.38 16.23,10.56" fill="#fff" />
                      </svg>
                    )}
                  </span>
                )}
              </div>

              {requestError && <p className="tvup-credit-request-error" role="alert"><ErrorRoundedIcon /> {requestError}</p>}
            </div>

            <footer className="tvup-credit-footer">
              <div className="tvup-credit-payment-actions">
                <button
                  type="submit"
                  ref={cardButtonRef}
                  className="tvup-credit-submit is-card"
                  disabled={!amountIsValid || loading}
                >
                  {loading && activePaymentMethod === 'card' ? (
                    <><CircularProgress size={18} thickness={5} /> Hazırlanıyor</>
                  ) : (
                    <>Kredi Kartıyla Öde <ArrowForwardRoundedIcon /></>
                  )}
                </button>
                <button
                  type="button"
                  className="tvup-credit-submit is-crypto"
                  disabled={!amountIsValid || loading}
                  onClick={handleCryptoCheckout}
                >
                  {loading && activePaymentMethod === 'crypto' ? (
                    <><CircularProgress size={18} thickness={5} /> Hazırlanıyor</>
                  ) : (
                    <>Kripto ile Öde <ArrowForwardRoundedIcon /></>
                  )}
                </button>
              </div>
              <p className="tvup-credit-form-security"><LockRoundedIcon /> Kart bilgileri Tivigo tarafından saklanmaz; kripto ödemesi Payizone üzerinde tamamlanır.</p>
            </footer>
          </form>
        </div>
      )}

      {phase === 'checkout' && (
        <div className="tvup-credit-checkout" role="status" aria-live="polite">
          {previewMode ? (
            <div className="tvup-credit-preview-checkout">
              <div className="tvup-credit-preview-window">
                <div className="tvup-credit-preview-window-bar"><i /><i /><i /><span>Güvenli ödeme</span></div>
                <div className="tvup-credit-preview-window-body">
                  <span className="tvup-credit-preview-lock"><SecurityRoundedIcon /></span>
                  <small>TASARIM ÖNİZLEMESİ</small>
                  <h3>Ödeme sağlayıcısının güvenli ekranı burada açılır.</h3>
                  <p>Gerçek kullanımda kart bilgileri yalnızca ödeme sağlayıcısının iframe ekranına girilir.</p>
                  <div><LockRoundedIcon /> 3D Secure korumalı bağlantı</div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`tvup-credit-iframe-wrap${isPayizoneCryptoCheckout ? ' is-payizone-crypto' : ''}`}
              aria-busy={!iframeReady}
              style={{ '--tvup-credit-iframe-height': `${checkoutFrameHeight}px` }}
            >
              {!iframeReady && (
                <div className="tvup-credit-iframe-loading">
                  <CircularProgress size={38} thickness={4} />
                  <b>Güvenli ödeme ekranı hazırlanıyor</b>
                  <span>Lütfen bekleyin...</span>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={checkoutUrl}
                title={isPayizoneCryptoCheckout ? 'Payizone kripto ödeme ekranı' : 'Güvenli kredi yükleme ödemesi'}
                allow="payment; clipboard-write"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={handleCheckoutIframeLoad}
              />
            </div>
          )}
        </div>
      )}

      {phase === 'result' && paymentResult && (
        <div className={`tvup-credit-result${paymentResult.success ? ' is-success' : ' is-error'}`} role="status" aria-live="polite">
          <span className="tvup-credit-result-icon">
            {paymentResult.success ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />}
          </span>
          {paymentResult.success && <small>ÖDEME TAMAMLANDI</small>}
          <h3>{paymentResult.success ? 'Teşekkürler!' : 'Ödemeyi alamadık.'}</h3>
          <p>
            {paymentResult.success
              ? 'Krediniz hesabınıza eklendi. Artık paket işlemlerinizde kullanabilirsiniz.'
              : paymentResult.message || 'Ödeme işlemi tamamlanamadı. Lütfen yeniden deneyin.'}
          </p>
          {paymentResult.success && <strong>{formatCredit(paymentResult.amount)} TL</strong>}
          <button type="button" onClick={paymentResult.success ? handleExplicitClose : handleRetry}>
            {paymentResult.success ? 'Tamam' : 'Tekrar Dene'}
          </button>
        </div>
      )}

      {open && cardConfirmation && (
        <div
          className="tvup-credit-confirm-overlay"
          role="region"
          aria-labelledby="tvup-credit-confirm-title"
          aria-describedby="tvup-credit-confirm-description"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setCardConfirmation(null);
            }
          }}
        >
          <header className="tvup-credit-modal-header tvup-credit-confirm-header">
            <div className="tvup-credit-title-icon" aria-hidden="true"><AccountBalanceWalletRoundedIcon /></div>
            <div className="tvup-credit-title-copy">
              <p>ÖDEME ONAYI</p>
              <h2 id="tvup-credit-confirm-title">Ödeme Tutarını Onaylayın</h2>
            </div>
            <button ref={confirmationFocusRef} className="tvup-credit-close" type="button" aria-label="Ödeme onayını kapat" onClick={() => setCardConfirmation(null)}><CloseRoundedIcon /></button>
          </header>
          <div className="tvup-credit-form tvup-credit-confirm-body">
            <div className="tvup-credit-form-content">
              <dl className="tvup-credit-confirm-amounts">
                <div>
                  <dt className="tvup-credit-amount-label">Yüklenecek Tutar</dt>
                  <dd className="tvup-credit-confirm-amount-field"><output className="tvup-credit-confirm-amount-value">{formatCredit(cardConfirmation.nativeAmount)} </output><span className="tvup-credit-confirm-currency">€</span></dd>
                </div>
                <div>
                  <dt className="tvup-credit-amount-label">Kartınızdan Çekilecek Tutar</dt>
                  <dd className="tvup-credit-confirm-amount-field"><output className="tvup-credit-confirm-amount-value">{formatCredit(cardConfirmation.amountTry)} </output><span className="tvup-credit-confirm-currency">TL</span></dd>
                </div>
              </dl>
              <div className="tvup-credit-info-note tvup-credit-confirm-note">
                <div className="tvup-credit-info-copy">
                  <b>Sadece Türk kartlarıyla ödeme yapabilirsiniz.</b>
                  <span id="tvup-credit-confirm-description">Yabancı banka kartıyla ödeme yapmak için canlı destekle iletişime geçin. <span className="tvup-credit-confirm-rate">1 Euro, {cardConfirmation.rate.toLocaleString('tr-TR', { maximumFractionDigits: 4 })} TL olarak çevrilmiştir.</span> Ödemeniz Türk lirası olarak alınacaktır. {formatCredit(cardConfirmation.nativeAmount)} € tutarındaki kredi yüklemeniz için kartınızdan {formatCredit(cardConfirmation.amountTry)} TL tahsil edilecektir.</span>
                </div>
              </div>
            </div>
            <footer className="tvup-credit-footer">
              <div className="tvup-credit-payment-actions tvup-credit-confirm-footer">
                <button className="tvup-credit-submit" type="button" onClick={confirmCardCheckout} disabled={loading}>Onaylıyorum <ArrowForwardRoundedIcon /></button>
                <button className="tvup-credit-submit tvup-credit-confirm-back" type="button" onClick={() => setCardConfirmation(null)}>Geri Dön</button>
              </div>
            </footer>
          </div>
          <ModalOpeningLoader duration={1000} />
        </div>
      )}

      <Dialog
        open={profileNameDialogOpen}
        maxWidth={false}
        transitionDuration={0}
        disableEscapeKeyDown={profileNameSaving}
        onClose={closeProfileNameDialog}
        className="tvup-credit-profile-dialog"
        aria-labelledby="tvup-credit-profile-title"
        aria-describedby="tvup-credit-profile-description"
        slotProps={{
          backdrop: { className: 'tvup-credit-profile-backdrop' },
          paper: {
            id: 'tvup-credit-profile-modal',
            className: 'tvup-credit-profile-modal',
          },
        }}
      >
        <form className="tvup-credit-profile-form" onSubmit={handleProfileNameSubmit} noValidate>
          <header className="tvup-credit-profile-header">
            <span aria-hidden="true"><BadgeOutlinedIcon /></span>
            <div>
              <h3 id="tvup-credit-profile-title">Ad Soyad Bilginizi Tamamlayın</h3>
              <p id="tvup-credit-profile-description">Kripto ödemeye devam etmek için ad soyad bilginizi girin.</p>
            </div>
            <button
              type="button"
              aria-label="Ad soyad penceresini kapat"
              disabled={profileNameSaving}
              onClick={closeProfileNameDialog}
            >
              <CloseRoundedIcon />
            </button>
          </header>
          <div className="tvup-credit-profile-body">
            <p>Bu bilgi profilinize güvenli biçimde kaydedilecek ve ödeme otomatik olarak devam edecektir.</p>
            <label htmlFor="tvup-credit-profile-name">Ad Soyad</label>
            <input
              id="tvup-credit-profile-name"
              type="text"
              maxLength={50}
              autoComplete="name"
              autoFocus
              value={profileNameValue}
              aria-invalid={Boolean(profileNameError)}
              aria-describedby={profileNameError ? 'tvup-credit-profile-error' : undefined}
              disabled={profileNameSaving}
              onChange={(event) => {
                setProfileNameValue(event.target.value);
                setProfileNameError('');
              }}
            />
            {profileNameError && (
              <p id="tvup-credit-profile-error" className="tvup-credit-profile-error" role="alert">
                <ErrorRoundedIcon /> {profileNameError}
              </p>
            )}
          </div>
          <footer className="tvup-credit-profile-footer">
            <button type="button" disabled={profileNameSaving} onClick={closeProfileNameDialog}>Vazgeç</button>
            <button type="submit" disabled={profileNameSaving || !profileNameValue.trim()}>
              {profileNameSaving ? (
                <><CircularProgress size={17} thickness={5} /> Kaydediliyor</>
              ) : (
                <>Kaydet ve Kripto Ödemeye Devam Et <ArrowForwardRoundedIcon /></>
              )}
            </button>
          </footer>
        </form>
      </Dialog>
    </Dialog>
  );
}

export default UserPanelCreditModal;
